import { useLocalStorage } from '@vueuse/core';
import useBrandingApi from '../api/useBrandingApi';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import { useBrandingButtonLink } from './UseBrandingButtonLink';
import { useBrandingStore } from '~/stores/branding/branding-store';
import type { FileType, SnackbarMessage } from '~/types';
import { BrandLogoType } from '~/enums/branding/brand-tab.enum';
import type { PartnerConfigModel } from '~/types/branding/partner-config';
import { buttonRadiusStyles, inputDesignStyles, inputRadiusStyles } from '~/constants/branding';
import { useButtonItemsStore } from '~/stores/branding/button-items-store';
import { logoResolutionSizes } from '~/constants/branding/logo-resolutions';

export const useBranding = () => {
  const { t } = useI18n();
  const partnerName = useLocalStorage<string>('partnerName', '');
  const { getConfigFromS3, getBrandingLogos, postBrandingConfig } = useBrandingApi();
  const {
    flattenButtonLinkItemsStyles,
    resetButtonLinkItemsColors,
    transformButtonLinkItemsStyles
  } = useBrandingButtonLink();
  const {
    partnerConfig,
    horizontalLogoUrl,
    uploadedHorizontalLogo,
    squareLogoUrl,
    uploadedSquareLogo,
    isUploadMode,
    productionPrimaryColor,
    productionSecondaryColor,
    isResetLoading,
    inputBorderRadius,
    inputDesign
  } = storeToRefs(useBrandingStore());
  const {
    buttonsBorderRadius,
    primaryButtonStyles,
    secondaryButtonStyles,
    linkStyles,
    prodPrimaryButtonStyles,
    prodSecondaryButtonStyles,
    prodLinkStyles,
    buttonLinkItemsState
  } = storeToRefs(useButtonItemsStore());
  const { loadArray } = storeToRefs(useStore());
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { fileToBase64, deepEqual } = useUtils();

  const brandingTabs = computed(() => {
    return [
      {
        label: t('branding.brand.aboutBranding.title'),
        value: 'about',
        permission: true
      },
      {
        label: t('branding.brand.logos.title'),
        value: 'logos',
        permission: true
      },
      {
        label: t('branding.brand.colors.title'),
        value: 'colors',
        permission: true
      },
      {
        label: t('branding.brand.buttonItems.title'),
        value: 'buttons-and-links',
        permission: true
      },
      {
        label: t('branding.brand.formItems.title'),
        value: 'form-items',
        permission: true
      }
    ];
  });
  const hasCustomStyles = computed(
    () =>
      !deepEqual(primaryButtonStyles.value, prodPrimaryButtonStyles.value) ||
      !deepEqual(secondaryButtonStyles.value, prodSecondaryButtonStyles.value) ||
      !deepEqual(linkStyles.value, prodLinkStyles.value)
  );

  const buildBrandingLogosBlob = async (logoName: string, response: Response) => {
    let contentType = response.headers.get('content-type') ?? '';
    let fileBlob;
    if (contentType.startsWith('image/')) {
      if (contentType === 'image/svg') {
        const textContent = await response.text();
        contentType = 'image/svg+xml';
        fileBlob = new Blob([textContent], { type: contentType });
      } else {
        fileBlob = await response.blob();
      }
    } else {
      const textContent = await response.text();
      fileBlob = new Blob([textContent], { type: contentType });
    }
    const file = new File([fileBlob], logoName, { type: contentType });
    return {
      fileType: {
        file,
        name: logoName,
        size: file.size,
        type: file.type,
        isValid: true
      },
      url: URL.createObjectURL(fileBlob)
    };
  };

  const loadBrandingLogos = async (
    type: string,
    logoUrl: globalThis.Ref<string | undefined>,
    uploadedLogo: globalThis.Ref<FileType | undefined>
  ) => {
    let response;
    const logoType =
      type === BrandLogoType.Square
        ? partnerConfig.value?.logo_square?.type
        : partnerConfig.value?.logo?.type;
    let logoName = `${type === BrandLogoType.Square ? 'logo_square' : 'logo'}.${logoType ?? 'svg'}`;
    response = await getBrandingLogos(
      partnerName.value,
      logoName,
      !logoType ? `/svg/${logoName}` : undefined
    );

    if (!response.ok) {
      logoName = `${type === BrandLogoType.Square ? 'logo_square' : 'logo'}.svg`;
      response = await getBrandingLogos(partnerName.value, logoName, `/svg/${logoName}`);
    }

    const { fileType, url } = await buildBrandingLogosBlob(logoName, response);
    logoUrl.value = url;
    uploadedLogo.value = fileType;
    loadArray.value.splice(loadArray.value.indexOf('getLogosFromS3'), 1);
  };

  const resetConfigToDefault = () => {
    partnerConfig.value = {
      colors: {
        primary: '#335584',
        secondary: '#59EE7E'
      },
      styles: {
        'primary-color': '#335584',
        'secondary-color': '#59EE7E',
        fieldsDesign: 'flat',
        fieldsBorderRadius: 'rounded',
        buttonsBorderRadius: 'rounded',
        buttonLinkItemsState: 'default'
      }
    };
    productionPrimaryColor.value = '#335584';
    productionSecondaryColor.value = '#59EE7E';
    inputBorderRadius.value = 'rounded';
    inputDesign.value = 'flat';
  };

  const setupPartnerConfig = async () => {
    try {
      const response = await getConfigFromS3(partnerName.value);
      partnerConfig.value = (await response.json()) as PartnerConfigModel;
      productionPrimaryColor.value = partnerConfig.value.colors.primary;
      productionSecondaryColor.value = partnerConfig.value.colors.secondary;
      inputDesign.value = partnerConfig.value.styles?.fieldsDesign || 'flat';
      inputBorderRadius.value = partnerConfig.value.styles?.fieldsBorderRadius || 'rounded';
      buttonsBorderRadius.value = partnerConfig.value.styles?.buttonsBorderRadius || 'rounded';
      buttonLinkItemsState.value = partnerConfig.value.styles?.buttonLinkItemsState || 'default';
      transformButtonLinkItemsStyles('primary', primaryButtonStyles, prodPrimaryButtonStyles);
      transformButtonLinkItemsStyles('secondary', secondaryButtonStyles, prodSecondaryButtonStyles);
      transformButtonLinkItemsStyles('link', linkStyles, prodLinkStyles);
    } catch {
      resetConfigToDefault();
    } finally {
      loadArray.value.splice(loadArray.value.indexOf('getConfigFromS3'), 1);
    }
  };

  const initBrandingLogos = async () => {
    await setupPartnerConfig();
    await loadBrandingLogos('horizontal', horizontalLogoUrl, uploadedHorizontalLogo);
    await loadBrandingLogos('square', squareLogoUrl, uploadedSquareLogo);
  };

  const handleUpdateEdit = (isEditMode: boolean, isBrandingLogos: boolean) => {
    isUploadMode.value = isEditMode;
    if (!isEditMode) {
      if (isBrandingLogos) {
        initBrandingLogos();
      } else {
        setupPartnerConfig();
      }
    }
  };

  const convertLogo = async (logo: FileType) => {
    const extension = logo.name.split('.').pop() || 'svg';
    const data = await fileToBase64(logo.file);
    return { type: extension, data };
  };

  const addBase64LogosToBody = async (withData = true) => {
    if (partnerConfig.value && uploadedSquareLogo.value && uploadedHorizontalLogo.value) {
      partnerConfig.value = {
        ...partnerConfig.value,
        logo: {
          type: (await convertLogo(uploadedHorizontalLogo.value)).type,
          ...(withData && { data: (await convertLogo(uploadedHorizontalLogo.value)).data })
        },
        logo_square: {
          type: (await convertLogo(uploadedSquareLogo.value)).type,
          ...(withData && { data: (await convertLogo(uploadedSquareLogo.value)).data })
        }
      };
    }
  };

  const addFormItemsStylesToBody = (isBrandingColors: boolean) => {
    if (partnerConfig.value) {
      if (isBrandingColors && buttonLinkItemsState.value === 'default') {
        resetButtonLinkItemsColors();
      }
      partnerConfig.value.styles = {
        ...partnerConfig.value.styles,
        'primary-color': partnerConfig.value.colors.primary,
        'secondary-color': partnerConfig.value.colors.secondary,
        fieldsDesign: inputDesign.value,
        fieldsBorderRadius: inputBorderRadius.value,
        buttonsBorderRadius: buttonsBorderRadius.value,
        ...inputDesignStyles[inputDesign.value],
        ...inputRadiusStyles[inputBorderRadius.value],
        ...buttonRadiusStyles[buttonsBorderRadius.value],
        ...flattenButtonLinkItemsStyles('primary-button', primaryButtonStyles.value),
        ...flattenButtonLinkItemsStyles('secondary-button', secondaryButtonStyles.value),
        ...flattenButtonLinkItemsStyles('link', linkStyles.value),
        buttonLinkItemsState: hasCustomStyles.value ? 'custom' : buttonLinkItemsState.value
      };
    }
  };

  const uploadBrandingConfig = async (
    isBrandingLogos: boolean = false,
    isBrandingColors: boolean = false
  ) => {
    try {
      await addBase64LogosToBody(isBrandingLogos);
      addFormItemsStylesToBody(isBrandingColors);
      const body = { ...partnerConfig.value } as PartnerConfigModel;
      await postBrandingConfig(body);
      handleUpdateEdit(false, isBrandingLogos);
      add({
        type: 'success',
        message: t('branding.brand.uploadSuccess')
      } as SnackbarMessage);
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const resetLogosToDefaultsPreview = async () => {
    isResetLoading.value = true;
    try {
      if (partnerConfig.value) {
        partnerConfig.value = {
          ...partnerConfig.value,
          logo: undefined,
          logo_square: undefined
        };

        await loadBrandingLogos('horizontal', horizontalLogoUrl, uploadedHorizontalLogo);
        await loadBrandingLogos('square', squareLogoUrl, uploadedSquareLogo);
      }
    } finally {
      isResetLoading.value = false;
    }
  };

  const resetColorsToDefaultsPreview = () => {
    isResetLoading.value = true;
    try {
      const defaultPrimaryColor = '#335584';
      const defaultSecondaryColor = '#59EE7E';

      if (partnerConfig.value) {
        partnerConfig.value = {
          ...partnerConfig.value,
          colors: {
            primary: defaultPrimaryColor,
            secondary: defaultSecondaryColor
          }
        };
      }
    } finally {
      isResetLoading.value = false;
    }
  };
  async function detectTransparency(file: File): Promise<boolean> {
    const checkCanvasTransparency = (src: string): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const cleanup = () => {
          URL.revokeObjectURL(src);
        };

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) {
              cleanup();
              return resolve(false);
            }

            ctx.drawImage(img, 0, 0);
            const sampleSize = 10;
            const width = img.width;
            const height = img.height;
            const corners = [
              { x: 0, y: 0 },
              { x: width - sampleSize, y: 0 },
              { x: 0, y: height - sampleSize },
              { x: width - sampleSize, y: height - sampleSize }
            ];
            for (const corner of corners) {
              const imageData = ctx.getImageData(
                corner.x,
                corner.y,
                Math.min(sampleSize, width - corner.x),
                Math.min(sampleSize, height - corner.y)
              );
              const { data } = imageData;
              for (let i = 3; i < data.length; i += 4) {
                if (data[i] < 255) {
                  cleanup();
                  return resolve(true);
                }
              }
            }
            cleanup();
            resolve(false);
          } catch {
            cleanup();
            resolve(false);
          }
        };

        img.onerror = () => {
          cleanup();
          reject(new Error('Failed to load image'));
        };

        img.src = src;
      });
    };

    if (file.type === 'image/png' || file.type === 'image/svg+xml') {
      const url = URL.createObjectURL(file);
      try {
        return await checkCanvasTransparency(url);
      } catch (error) {
        URL.revokeObjectURL(url);
        throw error;
      }
    }

    return false;
  }

  function detectResolution(
    file: File,
    isHorizontal: boolean
  ): Promise<{ width: number; height: number; status: 'pass' | 'warning' | 'error' }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const cleanup = () => {
        URL.revokeObjectURL(img.src);
      };

      img.onload = () => {
        const { width, height } = img;

        const warningThresholds = isHorizontal
          ? logoResolutionSizes.horizontal.warning
          : logoResolutionSizes.square.warning;

        const errorThresholds = isHorizontal
          ? logoResolutionSizes.horizontal.error
          : logoResolutionSizes.square.error;

        let status: 'pass' | 'warning' | 'error' = 'pass';
        if (width < errorThresholds.width || height < errorThresholds.height) {
          status = 'error';
        } else if (width < warningThresholds.width || height < warningThresholds.height) {
          status = 'warning';
        }
        cleanup();
        resolve({ width, height, status });
      };

      img.onerror = () => {
        cleanup();
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file);
    });
  }

  return {
    brandingTabs,
    setupPartnerConfig,
    loadBrandingLogos,
    initBrandingLogos,
    handleUpdateEdit,
    uploadBrandingConfig,
    resetLogosToDefaultsPreview,
    resetColorsToDefaultsPreview,
    detectTransparency,
    detectResolution
  };
};
